import React from 'react'
import get from 'lodash/get'
import omitDeep from 'omit-deep-lodash'
import { withRouter } from 'react-router-dom'
import { Constants } from '@sukiai/components'
import { compose, graphql, withApollo } from 'react-apollo'
import { UpdateScript, DeleteScript } from '@sukiai/gql/app'
import Editor, { V2_DEFAULT_CONTENT } from '@sukiai/quill-editor'

import {
  LO_STO,
  setError,
  setToast,
  HALF_STORY,
  EDITOR_TYPE,
  showSettings,
  SETTINGS_VIEW,
  isAmwellTheme,
  INTERCOM_EVENTS,
  isOrgEmrIntegrated,
  isEmbedScriptsView,
  showScripts,
  isNoteView
} from '../../lib'
import {
  GET_INSERT_SCRIPT_RES,
  GET_CURRENT_COMPOSITION,
  SET_INSERT_SCRIPT_PENDING,
  SET_MODAL
} from '../../apollo/actions'
import {
  Body,
  Button,
  Footer,
  Divider,
  Platter,
  Container,
  ActionText,
  ActionButton,
  SectionBlock,
  ScriptNameTip,
  HeaderActions,
  SectionOptions,
  ScriptNameInput,
  CloseModalButton,
  InvisibleContainer,
  CloseModalButtonDark,
  AmwellScriptHeader,
  AmwellBody,
  AmwellFooter,
  AmwellContent,
  AmwellPlatter,
  AmwellContainer,
  AmwellActionText,
  AmwellActionButton,
  AmwellInvisibleContainer,
  AmwellScriptNameInput,
  AmwellScriptHeading,
  AmwellSectionOptions,
  AmwellScriptOption
} from './styled'
import loSto from '../../config/loSto'
import { getStreamWorker } from '../../ws/stream'
import { DataCommand } from '../../ws/DataCommand'
import DeleteItemModal from '../DeleteItemModal'
import { logger, halfStory as hs, logIntercomEvent } from '../../lib/logging'

const { TOAST, MODALS } = Constants

let disableAction = false

class ScriptEditor extends React.Component {
  constructor (props) {
    super(props)

    const scriptEditorContent = get(props, 'script.blocks').map((b) => ({
      sectionId: b.sectionId,
      content: b.content
    }))

    const sections = props.script?.blocks
    const scriptName = props.modal.content.heading
    const blocks =
      sections &&
      sections.map((s) => ({
        sectionId: s.sectionId,
        content: s.content,
        sectionName: s.sectionName
      }))

    this.state = {
      scriptName,
      uneditedScriptName: scriptName,
      blocks,
      originalBlocks: blocks,
      sections: sections,
      editMode: false,
      editorContent: scriptEditorContent,
      undoChange: false,
      showDeleteModal: false,
      isAmwellDark: isAmwellTheme()
    }
  }

  componentDidUpdate (prevProps) {
    if (this.state.undoChange) {
      this.setState({ undoChange: false })
    }
  }

  componentDidMount = async () => {
    loSto.set(LO_STO.SETTINGS_VIEW, SETTINGS_VIEW.SCRIPTS)
    document.addEventListener('mousedown', this.handleClick)
  };

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleClick)
  }

  handleClick = (e) => {
    const { sectionOptionsOpen } = this.state
    if (!sectionOptionsOpen) {
      return
    }
    const isSectionDropDown = e.target.classList.contains('section-dropdown')
    if (!isSectionDropDown) {
      this.setState({ sectionOptionsOpen: false })
    }
  };

  componentWillReceiveProps (nextProps) {
    if (
      !nextProps.insertScriptRes?.isPending &&
      this.props.insertScriptRes?.isPending
    ) {
      if (nextProps.insertScriptRes.error) {
        const error = nextProps.insertScriptRes.error
        const compositionId = loSto.get(LO_STO.CURRENT_COMP_ID)

        console.warn(
          `Error inserting script ID ${nextProps.scriptId} into composition ID ${compositionId}:`,
          error
        )
        nextProps.dismiss()
      } else {
        nextProps.dismiss()
        // console.info(`Inserted script ID ${scriptId} into composition ID ${compositionId}.`)
        // hs.add(HALF_STORY.SCRIPT_INSERTED, `Inserted script ${scriptId} via physician script editor`)
        logIntercomEvent(INTERCOM_EVENTS.SCRIPT_INSERTED)
      }
    }

    nextProps.shouldDismiss && this.props.open && nextProps.removeModal()
  }

  insertScript = async () => {
    const { client, scriptId } = this.props

    const compositionId = loSto.get(LO_STO.CURRENT_COMP_ID)

    // get notetypeid
    const { composition } = await client.readQuery({
      query: GET_CURRENT_COMPOSITION
    })
    const comp = JSON.parse(composition, null, 2)

    client.mutate({
      mutation: SET_INSERT_SCRIPT_PENDING,
      variables: {
        isPending: true
      }
    })

    hs.add(
      HALF_STORY.CLICK_INSERT_SCRIPT,
      'Click Insert Script in Doctor Script Editor'
    )
    const stream = getStreamWorker()
    stream &&
      stream.postMessage({
        cmd: DataCommand.USER_REQUEST,
        userRequestType: 'CLICK_INSERT_SCRIPT',
        userRequestData: {
          compositionId,
          notetypeId: comp?.metadata?.noteTypeId,
          scriptId: scriptId
        },
        ts: new Date().toISOString()
      })
  };

  handleUpdateScript = () => {
    const { client, script, scriptId } = this.props
    const { scriptName, blocks } = this.state

    this.setState({ updatePending: true })

    const newTags = omitDeep(get(script, 'tags'), '__typename')
    newTags[0].name.value = scriptName

    const emptyBlocks = blocks.some((block) => {
      const a = JSON.stringify(JSON.parse(block.content))
      const b = JSON.stringify(JSON.parse(V2_DEFAULT_CONTENT))
      return a === b
    })
    if (emptyBlocks) {
      setToast(
        TOAST.TYPE.WARNING,
        'Add some script content!',
        TOAST.DURATION.MEDIUM
      )
      this.setState({ updatePending: false })
      return
    }

    const blocksToInsert = blocks.map((b) => ({
      sectionId: b.sectionId,
      content: b.content,
      name: b.scriptName
    }))

    client
      .mutate({
        mutation: UpdateScript,
        variables: {
          organizationId: loSto.get(LO_STO.ORGANIZATION_ID),
          id: scriptId,
          userId: loSto.get(LO_STO.USER_ID),
          tags: newTags,
          blocks: blocksToInsert
        }
      })
      .then(() => {
        console.info('Successfully updated script')
        logger.info('Updated script via physician script editor')
        hs.add(
          HALF_STORY.SCRIPT_UPDATED,
          `Updated script ${scriptId} via physician script editor`
        )
        this.setState({
          uneditedScriptName: scriptName,
          originalBlocks: blocks,
          editMode: false,
          updatePending: false
        })
        setToast(TOAST.TYPE.PRIMARY, 'Script updated', TOAST.DURATION.MEDIUM)
        // TODO: Eventually show a toast to indicate succesful editing of script
      })
      .catch((err) => {
        this.setState({ updatePending: false })

        const errMessage = get(err, 'graphQLErrors[0].message', '')
        if (errMessage.includes('has an existing macro')) {
          setToast(
            TOAST.TYPE.WARNING,
            'Script with that name already exists. Please change the name and try again.',
            TOAST.DURATION.MEDIUM
          )
          return
        }

        setToast(
          TOAST.TYPE.ERROR,
          'Error updating Script. Please try again.',
          TOAST.DURATION.MEDIUM
        )
        console.error('Error updating script', err)
        logger.error('Error updating script via physician script editor')
        // TODO: Eventually display a toast indicating unsuccessful editing of script
        // instead of closing modal
      })
  };

  handleClose = () => {
    const { match, client } = this.props
    if (isEmbedScriptsView(match.url)) {
      client.mutate({
        mutation: SET_MODAL,
        variables: {
          content: {
            __typename: 'ModalContent',
            type: MODALS.AMWELL_SCRIPTS
          }
        }
      })
    } else if (isNoteView()) {
      showScripts()
    } else {
      showSettings()
    }
  };

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value })
  };

  onEditorChange = (newValue, sectionId) => {
    const { blocks } = this.state

    const newBlocks = blocks.slice()
    const idx = newBlocks.findIndex((s) => s.sectionId === sectionId)
    if (idx < 0) return
    newBlocks[idx].content = newValue
    this.setState({ blocks: newBlocks })
  };

  onSelectSection = (blockIdx, sectionId, e) => {
    e.stopPropagation()
    const { blocks } = this.state
    const newBlocks = [...blocks]
    newBlocks[blockIdx].sectionId = sectionId

    this.setState({
      blocks: newBlocks,
      sectionOptionsOpen: null
    })
  };

  openSectionSelection = (blockId) => {
    this.setState({ sectionOptionsOpen: blockId })
  };

  onHandleUnidecode = (event) => {
    const { scriptId } = this.props
    const { beforeText, afterText } = event
    console.log(
      `[Unidecode Conversion] - ScriptId: ${scriptId} | Before: ${beforeText} | After: ${afterText} `
    )

    logger.info(
      `[Unidecode Conversion] - ScriptId: ${scriptId} | Before: ${beforeText} | After: ${afterText} `
    )
    setToast(
      TOAST.TYPE.INFO,
      'Converted unsupported text',
      TOAST.DURATION.SHORT
    )
  };

  openDeleteModal = () => {
    this.setState({ showDeleteModal: true })
  };

  closeDeleteModal = () => {
    this.setState({ showDeleteModal: false })
  };

  deleteScript = () => {
    const { client, scriptId, dismiss } = this.props

    client
      .mutate({
        mutation: DeleteScript,
        variables: {
          organizationId: loSto.get(LO_STO.ORGANIZATION_ID),
          id: scriptId
        }
      })
      .then(() => {
        console.info('Successfully deleted script')
        logger.info('Deleted script via physician script editor')
        hs.add(
          HALF_STORY.SCRIPT_UPDATED,
          `Deleted script ${scriptId} via physician script editor`
        )
        this.handleClose()
        setToast(TOAST.TYPE.PRIMARY, 'Script deleted', TOAST.DURATION.MEDIUM)
        // TODO: Eventually show a toast to indicate succesful editing of script
      })
      .catch((err) => {
        dismiss()
        console.error('Error deleting script', err)
        logger.error('Error deleting script via physician script editor')
        setTimeout(() => {
          setError('Could not delete script. Please try again.')
        }, 400)
        // TODO: Eventually display a toast indicating unsuccessful editing of script
        // instead of closing modal
      })
  };

  enterEditMode = () => {
    this.setState({ editMode: true })
  };

  handleUndoEdits = () => {
    const { uneditedScriptName, originalBlocks } = this.state

    this.setState({
      blocks: originalBlocks,
      undoChange: true,
      editMode: false,
      scriptName: uneditedScriptName
    })
  };

  createActions = () => {
    const { script, match } = this.props
    const { editMode } = this.state
    const insertDisabled =
      window.location.pathname.split('/')[1] !== 'note' || editMode

    const actions = [
      {
        title: editMode ? 'Cancel' : 'Delete',
        action: editMode ? this.handleUndoEdits : this.openDeleteModal,
        alignLeft: true,
        data: editMode ? 'modal-btn-cancel' : 'modal-btn-delete',
        dataCy: editMode
          ? 'script-editor-btn-cancel'
          : 'script-editor-btn-delete',
        styles: { color: '#ff787a' }
      },
      {
        title: editMode ? 'Save' : 'Edit',
        action: editMode ? this.handleUpdateScript : this.enterEditMode,
        data: editMode ? 'modal-btn-save' : 'modal-btn-edit',
        dataCy: editMode ? 'script-editor-btn-save' : 'script-editor-btn-edit',
        disabled: false
      }
    ]

    if (!isEmbedScriptsView(match.url) && !editMode) {
      actions.push({
        title: 'Insert',
        action: this.insertScript,
        data: 'modal-btn-insert',
        dataCy: 'script-editor-btn-insert',
        disabled: insertDisabled
      })
    }

    return actions
  };

  renderNewSectionBlock = (block, blockIdx) => {
    const { allSections } = this.props
    const { sectionOptionsOpen, editMode, undoChange, originalBlocks } =
      this.state

    const section = allSections.find((s) => s.id === block.sectionId)

    let onClickPlatter = () => {}
    if (editMode) {
      onClickPlatter = (e) => this.openSectionSelection(block?.sectionId)
    }

    let scriptContent = block.content
    let sectionName = section?.name

    if (sectionName === 'Any Section') {
      sectionName = "Any section I'm dictating in"
    }

    if (undoChange) {
      const originalBlock = originalBlocks.find(
        (b) => b.sectionId === block.sectionId
      )
      console.log('uneditedBlock', originalBlock)

      sectionName = originalBlock.sectionName
      scriptContent = originalBlock.content
    }

    return (
      <SectionBlock>
        <Platter onClick={onClickPlatter} readonly={!editMode}>
          {editMode && <p className='label'>Insert In</p>}
          <p
            className={`section-name${this.state.isAmwellDark ? ' dark' : ''}`}
          >
            {sectionName}
          </p>
        </Platter>
        {sectionOptionsOpen && (
          <SectionOptions className='section-dropdown'>
            {allSections.map((section, idx) => {
              let sName = section?.name
              if (sName === 'Any Section') {
                sName = "Any section I'm dictating in"
              }
              return (
                <div
                  key={idx}
                  onClick={(e) => this.onSelectSection(blockIdx, section.id, e)}
                  className='section-dropdown'
                >
                  {sName}
                </div>
              )
            })}
          </SectionOptions>
        )}
        <Editor
          id='script-section-content'
          defaultValue={scriptContent}
          s2Mode={false}
          force={undoChange}
          persona='PHYSICIAN'
          editorType={EDITOR_TYPE.SCRIPT}
          readOnly={!editMode}
          onChange={(val) => this.onEditorChange(val, block.sectionId)}
          key={undoChange} // woah amazing hack!
          style={{
            fontSize: '10px',
            borderRadius: '4px',
            padding: '5px',
            boxShadow: editMode ? '0 0 2px 0 rgba(0, 0, 0, 0.3)' : 'none',
            maxHeight: '50vh'
          }}
          handleUnidecode={this.onHandleUnidecode}
          enableUnidecoding
          enableChips={isOrgEmrIntegrated()}
        />
      </SectionBlock>
    )
  };

  renderNewAmwellSectionBlock = (block, blockIdx) => {
    const { allSections } = this.props
    const { sectionOptionsOpen, editMode, undoChange, originalBlocks } =
      this.state

    const section = allSections.find((s) => s.id === block.sectionId)

    let onClickPlatter = () => {}
    if (editMode) {
      onClickPlatter = (e) => this.openSectionSelection(block?.sectionId)
    }

    let scriptContent = block.content
    let sectionName = section?.name

    if (sectionName === 'Any Section') {
      sectionName = "Any section I'm dictating in"
    }

    if (undoChange) {
      const originalBlock = originalBlocks.find(
        (b) => b.sectionId === block.sectionId
      )
      console.log('uneditedBlock', originalBlock)

      sectionName = originalBlock.sectionName
      scriptContent = originalBlock.content
    }

    return (
      <SectionBlock>
        <AmwellPlatter onClick={onClickPlatter} readOnly={!editMode}>
          <p className='label'>Insert In</p>
          <p className='section-name'>{sectionName}</p>
        </AmwellPlatter>
        {sectionOptionsOpen && (
          <AmwellSectionOptions className='section-dropdown'>
            {allSections.map((section, idx) => {
              let sName = section?.name
              if (sName === 'Any Section') {
                sName = "Any section I'm dictating in"
              }
              return (
                <AmwellScriptOption
                  key={idx}
                  onClick={(e) => this.onSelectSection(blockIdx, section.id, e)}
                  className='section-dropdown'
                >
                  {sName}
                </AmwellScriptOption>
              )
            })}
          </AmwellSectionOptions>
        )}
        <Editor
          id='script-section-content'
          defaultValue={scriptContent}
          s2Mode={false}
          force={undoChange}
          persona='PHYSICIAN'
          editorType={EDITOR_TYPE.SCRIPT}
          readOnly={!editMode}
          onChange={(val) => this.onEditorChange(val, block.sectionId)}
          key={undoChange} // woah amazing hack!
          style={{
            borderRadius: '4px',
            padding: '16px',
            backgroundColor: editMode ? 'rgba(255, 255, 255, 0.05)' : 'inherit'
          }}
          handleUnidecode={this.onHandleUnidecode}
          enableUnidecoding
          enableChips={isOrgEmrIntegrated()}
        />
      </SectionBlock>
    )
  };

  renderActionButton = (modalType) => (a, idx) => {
    return (
      <ActionButton
        key={idx}
        onPress={() => {
          if (!disableAction) {
            disableAction = true
            setTimeout(() => {
              disableAction = false
            }, 500)
            a.action()
          }
        }}
        alignleft={a.alignLeft ? 'true' : 'false'}
        disabled={a.disabled}
        padding='12px 0px'
      >
        <span data-cy={a.dataCy}>
          <ActionText
            type={modalType}
            disabled={a.disabled ? 'true' : 'false'}
            allcaps={
              modalType === MODALS.IM_DONE_BYPASS_QA ||
              modalType === MODALS.SCRIPT_DETAILS
                ? 'false'
                : 'true'
            }
          >
            {a.title}
          </ActionText>
        </span>
      </ActionButton>
    )
  };

  renderAmwellActionButton = (modalType) => (a, idx) => {
    return (
      <AmwellActionButton
        key={idx}
        onClick={() => {
          if (!disableAction) {
            disableAction = true
            setTimeout(() => {
              disableAction = false
            }, 500)
            a.action()
          }
        }}
        alignleft={a.alignLeft}
        disabled={a.disabled}
        style={a.styles}
      >
        <span data-cy={a.dataCy}>
          <AmwellActionText
            type={modalType}
            allcaps={
              modalType === MODALS.IM_DONE_BYPASS_QA ||
              modalType === MODALS.SCRIPT_DETAILS
                ? 'false'
                : 'true'
            }
          >
            {a.title}
          </AmwellActionText>
        </span>
      </AmwellActionButton>
    )
  };

  renderFooter = () => {
    const { modal } = this.props
    const { loading, type } = modal || {}
    const actions = this.createActions()
    if (loading) {
      return null
    }
    return <>{actions.map(this.renderActionButton(type))}</>
  };

  renderAmwellFooter = () => {
    const { modal } = this.props
    const { loading, type } = modal || {}
    const actions = this.createActions()
    if (loading) {
      return null
    }
    return <>{actions.map(this.renderAmwellActionButton(type))}</>
  };

  renderAmwellScriptHeader = () => {
    const { editMode } = this.state

    return (
      <AmwellScriptHeader editMode={editMode}>
        {editMode ? (
          <AmwellScriptHeading>Edit Script</AmwellScriptHeading>
        ) : (
          <CloseModalButtonDark onClick={this.handleClose}>
            <div id='mdiv'>
              <div className='mdiv'>
                <div className='md' />
              </div>
            </div>
          </CloseModalButtonDark>
        )}
      </AmwellScriptHeader>
    )
  };

  renderAmwellScriptEditor = () => {
    const { editMode, scriptName } = this.state

    return (
      <AmwellInvisibleContainer>
        <AmwellContainer
          isAmwellDark={this.state.isAmwellDark}
          onClick={(e) => e.stopPropagation()}
        >
          <AmwellBody readOnly={!editMode}>
            {this.renderAmwellScriptHeader()}
            <AmwellContent>
              <AmwellScriptNameInput
                name='scriptName'
                value={scriptName}
                type='text'
                onChange={this.handleChange}
                readOnly={!editMode}
              />
              {this.state.blocks.map((block, blockIdx) => {
                return this.renderNewAmwellSectionBlock(block, blockIdx)
              })}
            </AmwellContent>

            <AmwellFooter alignment='row'>
              {this.renderAmwellFooter()}
            </AmwellFooter>
          </AmwellBody>
        </AmwellContainer>
      </AmwellInvisibleContainer>
    )
  };

  renderDefaultScriptEditor = () => {
    const { editMode, scriptName } = this.state

    return (
      <InvisibleContainer>
        <Container
          isAmwellDark={this.state.isAmwellDark}
          onClick={(e) => e.stopPropagation()}
        >
          <Body readOnly={!editMode}>
            {!editMode && <CloseModalButton onClick={this.handleClose} />}
            {editMode && (
              <HeaderActions>
                <Button onClick={this.handleUndoEdits}>Cancel</Button>
                <Button onClick={this.handleUpdateScript}>Save</Button>
              </HeaderActions>
            )}
            <div className='content' style={{ width: '70%' }}>
              <ScriptNameInput
                isAmwellDark={this.state.isAmwellDark}
                name='scriptName'
                value={scriptName}
                type='text'
                onChange={this.handleChange}
                readOnly={!editMode}
              />
              {editMode && (
                <ScriptNameTip>
                  Choose a name that is easy to say and remember
                </ScriptNameTip>
              )}
              {this.state.blocks.map((block, blockIdx) => {
                return this.renderNewSectionBlock(block, blockIdx)
              })}
            </div>
            {!editMode && (
              <div style={{ marginTop: 'auto', width: '100%' }}>
                <Divider />
                <Footer alignment='row'>{this.renderFooter()}</Footer>
              </div>
            )}
          </Body>
        </Container>
      </InvisibleContainer>
    )
  };

  render () {
    const { showDeleteModal } = this.state
    return (
      <>
        {isAmwellTheme()
          ? this.renderAmwellScriptEditor()
          : this.renderDefaultScriptEditor()}
        {showDeleteModal && (
          <DeleteItemModal
            handleDelete={this.deleteScript}
            handleClose={this.closeDeleteModal}
            deleteString='Delete Script?'
          />
        )}
      </>
    )
  }
}

export default compose(
  graphql(GET_INSERT_SCRIPT_RES, {
    props: ({ data: { insertScriptRes } }) => ({
      insertScriptRes
    })
  }),
  withApollo,
  withRouter
)(ScriptEditor)
